import type { RequestDetailViewState } from "./request-detail-view-types";
import { AlertTriangle, CheckCircle2, Plus, Send, Sparkles, X } from "lucide-react";

type Props = Pick<
  RequestDetailViewState,
  | "evaluation"
  | "decisionOpen"
  | "decisionStep"
  | "decisionOfferId"
  | "decisionRationale"
  | "decisionError"
  | "setDecisionOpen"
  | "setDecisionStep"
  | "setDecisionOfferId"
  | "setDecisionRationale"
  | "submitDecision"
  | "submittingDecision"
  | "requestDecisionBusy"
  | "selectedDecisionOffer"
  | "systemDecisionOffer"
  | "systemDecisionRunnerUp"
  | "systemDecisionScoreGap"
  | "systemRecommendationSummary"
  | "systemRecommendationMetrics"
  | "rationalePrompts"
  | "eligibleDecisionOffers"
>;
export function RequestDecisionDialog({
  evaluation,
  decisionOpen,
  decisionStep,
  decisionOfferId,
  decisionRationale,
  decisionError,
  setDecisionOpen,
  setDecisionStep,
  setDecisionOfferId,
  setDecisionRationale,
  submitDecision,
  submittingDecision,
  requestDecisionBusy,
  selectedDecisionOffer,
  systemDecisionOffer,
  systemDecisionRunnerUp,
  systemDecisionScoreGap,
  systemRecommendationSummary,
  systemRecommendationMetrics,
  rationalePrompts,
  eligibleDecisionOffers,
}: Props) {
  return decisionOpen && evaluation ? (
        <>
          <button
            type="button"
            className="procurement-decision-backdrop"
            aria-label="Karar panelini kapat"
            disabled={submittingDecision}
            onClick={() => setDecisionOpen(false)}
          />
          <section
            className="form-modal-surface procurement-decision-modal procurement-decision-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="workspace-decision-title"
          >
            <div className="section-heading compact">
              <div>
                <span className="section-kicker">Satın alma kararı</span>
                <h2 id="workspace-decision-title">
                  {decisionStep === 1 ? "Tedarikçi seçimi" : "Karar gerekçesi"}
                </h2>
                <p className="modal-description">
                  {decisionStep === 1
                    ? "Karşılaştırmayı inceleyin ve onaya göndereceğiniz teklifi seçin."
                    : "Seçiminizi kısa, somut ve denetlenebilir bir gerekçeyle tamamlayın."}
                </p>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Karar penceresini kapat"
                disabled={submittingDecision}
                onClick={() => setDecisionOpen(false)}
              >
                <X size={17} />
              </button>
            </div>
            <div
              className="procurement-decision-progress"
              aria-label="Karar adımları"
            >
              <span
                className={decisionStep === 1 ? "is-active" : "is-complete"}
              >
                <b>1</b> Teklif seçimi
              </span>
              <i aria-hidden="true" />
              <span className={decisionStep === 2 ? "is-active" : ""}>
                <b>2</b> Gerekçe
              </span>
            </div>
            <div className="procurement-decision-body">
              {decisionError ? (
                <div className="offer-form-error" role="alert">
                  <AlertTriangle size={18} aria-hidden="true" />
                  <div>
                    <strong>Karar gönderilemedi</strong>
                    <span>{decisionError}</span>
                  </div>
                </div>
              ) : null}
              {decisionStep === 1 ? (
                <>
                  <section className="system-decision-explanation">
                    <div className="system-decision-heading">
                      <span className="system-decision-icon">
                        <Sparkles size={18} aria-hidden="true" />
                      </span>
                      <div>
                        <span>Sistem önerisi</span>
                        <strong>{systemDecisionOffer?.supplier}</strong>
                      </div>
                      <em>
                        {systemDecisionOffer?.totalScore === null ||
                        systemDecisionOffer?.totalScore === undefined
                          ? "—"
                          : Math.round(systemDecisionOffer.totalScore)}
                        <small>/100</small>
                      </em>
                    </div>
                    <p>{systemRecommendationSummary}</p>
                    <div className="system-decision-metrics">
                      {systemRecommendationMetrics.map((metric) => (
                        <div key={metric.label}>
                          <span>{metric.label}</span>
                          <strong>{metric.value}</strong>
                          <small>
                            {metric.score === null || metric.score === undefined
                              ? "Puanlanmadı"
                              : `${Math.round(metric.score)}/100`}
                          </small>
                        </div>
                      ))}
                    </div>
                    <div className="system-decision-comparison">
                      <span>En yakın alternatif</span>
                      <strong>
                        {systemDecisionRunnerUp?.supplier ??
                          "Karşılaştırılabilir teklif yok"}
                      </strong>
                      <small>
                        {systemDecisionScoreGap === null
                          ? "Karşılaştırma kapsamı sınırlı"
                          : `${systemDecisionScoreGap} puan önde`}
                      </small>
                    </div>
                  </section>
                  <fieldset className="decision-choice-group">
                    <legend>
                      <span>Değerlendirilen teklifler</span>
                      <small>
                        {eligibleDecisionOffers.length} geçerli teklif
                      </small>
                    </legend>
                    {eligibleDecisionOffers.map((offer, index) => {
                      const isSystemOffer = offer.id === evaluation.topOfferId;
                      const isSelected = offer.id === decisionOfferId;
                      return (
                        <button
                          key={offer.id}
                          type="button"
                          className={isSelected ? "is-selected" : ""}
                          aria-pressed={isSelected}
                          onClick={() => setDecisionOfferId(offer.id)}
                        >
                          <span
                            className="decision-offer-rank"
                            aria-hidden="true"
                          >
                            {isSelected ? (
                              <CheckCircle2 size={18} />
                            ) : (
                              (offer.rank ?? index + 1)
                            )}
                          </span>
                          <span className="decision-offer-content">
                            <span className="decision-offer-heading">
                              <span>
                                <strong>{offer.supplier}</strong>
                                <small>{offer.offerNo}</small>
                              </span>
                              {isSystemOffer ? <em>Sistem önerisi</em> : null}
                            </span>
                            <span className="decision-offer-metrics">
                              <span>
                                <small>Puan</small>
                                <strong>
                                  {offer.totalScore === null
                                    ? "—"
                                    : `${Math.round(offer.totalScore)}/100`}
                                </strong>
                              </span>
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </fieldset>
                </>
              ) : (
                <div className="decision-rationale-step">
                  <section className="selected-decision-summary">
                    <span>Seçilen teklif</span>
                    <strong>{selectedDecisionOffer?.supplier}</strong>
                    <small>
                      {selectedDecisionOffer?.offerNo} ·{" "}
                      {selectedDecisionOffer?.totalScore === null ||
                      selectedDecisionOffer?.totalScore === undefined
                        ? "—"
                        : Math.round(selectedDecisionOffer.totalScore)}
                      /100
                    </small>
                  </section>
                  <div className="decision-rationale-assist">
                    <strong>Gerekçeyi hızlı oluşturun</strong>
                    <small>
                      Uygun ifadeleri seçin; metne ekleyip düzenleyebilirsiniz.
                    </small>
                    <div>
                      {rationalePrompts.map((prompt) => (
                        <button
                          key={prompt.label}
                          type="button"
                          onClick={() =>
                            setDecisionRationale((current) =>
                              current.includes(prompt.text)
                                ? current
                                : [current.trim(), prompt.text]
                                    .filter(Boolean)
                                    .join(" "),
                            )
                          }
                        >
                          <Plus size={14} aria-hidden="true" />
                          {prompt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="decision-rationale-field">
                    Karar gerekçesi
                    <textarea
                      rows={6}
                      value={decisionRationale}
                      onChange={(event) =>
                        setDecisionRationale(event.target.value)
                      }
                      placeholder="Seçimin ticari ve operasyonel gerekçesini yazın."
                      autoFocus
                    />
                    <small>
                      {decisionRationale.trim().length < 10
                        ? `En az ${10 - decisionRationale.trim().length} karakter daha yazın.`
                        : "Gerekçe denetim kaydına eklenecek."}
                    </small>
                  </label>
                </div>
              )}
            </div>
            <div className="procurement-decision-actions">
              <button
                type="button"
                className="secondary-button"
                disabled={submittingDecision}
                onClick={() =>
                  decisionStep === 2
                    ? setDecisionStep(1)
                    : setDecisionOpen(false)
                }
              >
                {decisionStep === 2 ? "Geri" : "Vazgeç"}
              </button>
              <button
                type="button"
                className="approve-button"
                disabled={
                  requestDecisionBusy ||
                  !decisionOfferId ||
                  (decisionStep === 2 && decisionRationale.trim().length < 10)
                }
                onClick={() =>
                  decisionStep === 1
                    ? setDecisionStep(2)
                    : void submitDecision()
                }
              >
                <Send size={16} />
                {submittingDecision
                  ? "Gönderiliyor"
                  : decisionStep === 1
                    ? "Gerekçeye geç"
                    : "Onaya gönder"}
              </button>
            </div>
          </section>
        </>
      ) : null;
}
